/* eslint-disable no-nested-ternary */
import React from "react";
import moment from "moment";
import { FormattedMessage } from "react-intl";

import useFeatureFlag from "hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "constants/featureFlags";
import { StopTimeType } from "types/MultiStop";
import CardItem from "./CardItem";

interface IStop {
  stop_type: number;
  stop_time_type: number;
  country_name?: string;
  city_name?: string;
  stop_airport_code?: string | null;
  stop_seaport_code?: string | null;
  stop_after_time?: string | null;
  stop_after_date?: string | null;
  stop_before_time?: string | null;
  stop_before_date?: string | null;
  stop_key?: string;
}

interface IMultiStopLocationCardProps {
  stop: IStop;
  index: number;
  totalStops: number;
}

function formatDateTime(time: string, date: string): string {
  return `${moment(date).format("DD MMM")}, ${moment(time, "HH:mm:ss").format("HH:mm")}`;
}

function getStopLabel(index: number, totalStops: number): React.ReactNode {
  if (index === 0) {
    return <FormattedMessage id="new-request.schedule.specifyHoursFrom" defaultMessage="From" />;
  }
  if (index === totalStops - 1) {
    return <FormattedMessage id="new-request.schedule.specifyHoursTo" defaultMessage="To" />;
  }
  return <FormattedMessage id="new-request.schedule.stop" defaultMessage="Stop {number}" values={{ number: index }} />;
}

function StopSchedule({ stop }: { stop: IStop }) {
  switch (stop.stop_time_type) {
    case StopTimeType.ASAP_DELIVERY:
      return (
        <span>
          <FormattedMessage id="new-request.schedule.defaultCargoReadyOption.asap" defaultMessage="ASAP" />
        </span>
      );
    case StopTimeType.BEFORE:
      if (stop.stop_before_time && stop.stop_before_date) {
        return <span>{formatDateTime(stop.stop_before_time, stop.stop_before_date)}</span>;
      }
      return null;
    case StopTimeType.BETWEEN:
      return (
        <>
          {stop.stop_after_time && stop.stop_after_date && (
            <span>{formatDateTime(stop.stop_after_time, stop.stop_after_date)}</span>
          )}
          {stop.stop_after_time && stop.stop_before_time && " - "}
          {stop.stop_before_time && stop.stop_before_date && (
            <span>{formatDateTime(stop.stop_before_time, stop.stop_before_date)}</span>
          )}
        </>
      );
    case StopTimeType.AFTER:
    default:
      if (stop.stop_after_time && stop.stop_after_date) {
        return <span>{formatDateTime(stop.stop_after_time, stop.stop_after_date)}</span>;
      }
      return null;
  }
}

function MultiStopLocationCard({ stop, index, totalStops }: IMultiStopLocationCardProps) {
  const isHeaderPortLabelingEnabled = useFeatureFlag(FEATURE_FLAGS.FF_WEB10382_HEADER_PORT_LABELING);
  const label = getStopLabel(index, totalStops);
  const locationCode = stop.stop_airport_code || stop.stop_seaport_code;
  const location = isHeaderPortLabelingEnabled
    ? locationCode || (stop.city_name && stop.country_name ? `${stop.city_name}, ${stop.country_name}` : stop.country_name)
    : (stop.city_name && stop.country_name ? `${stop.city_name}, ${stop.country_name}` : stop.country_name) || locationCode;

  return (
    <CardItem>
      <p className="font-b3 text-secondary">{label}</p>
      {location ? (
        <>
          <p className="font-b4">{location}</p>
          <p className="font-b3">
            <StopSchedule stop={stop} />
          </p>
        </>
      ) : (
        "-"
      )}
    </CardItem>
  );
}

export default MultiStopLocationCard;
